import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Table, Spin, Empty, Typography } from 'antd';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import api from '../../api';
import { UserContext } from '../../App';

// Fixed order, so the columns read as an escalation left to right.
const WARNINGS = ['1st', '2nd', '3rd', 'Final'];

/**
 * Compliance for a team, not a campaign.
 *
 * ComplianceIncidents is keyed on TeamID and nothing in the data joins a team
 * to a campaign, so this tab is scoped by the team picker rather than the
 * campaign one. Showing it under a campaign heading without saying so would
 * imply a filter that is not being applied.
 */
export default function ComplianceTab({ teamId, team, from, to }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const currentUser = useContext(UserContext);
  const navigate    = useNavigate();
  const canViewAgent = (currentUser?.CanViewAgentSummary ?? 0) >= 1;

  useEffect(() => {
    if (!teamId) { setData(null); return; }
    setLoading(true);
    api.get('/api/campaign-summary/compliance', {
      params: { team_id: teamId, start_date: from, end_date: to },
    })
      .then((r) => setData(r.data && typeof r.data === 'object' ? r.data : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teamId, from, to]);

  if (!teamId) {
    return <Empty description="Select a team" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }
  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }
  if (!data) return <Empty description="Nothing to show" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;

  const byMonth = Array.isArray(data.by_month) ? data.by_month : [];
  const byIssue = Array.isArray(data.by_issue) ? data.by_issue : [];

  // One row per agent, a column per warning -- same table as Compliance Stats,
  // without its Team column since this tab is already one team.
  const warnRows = Array.isArray(data.active_warnings) ? data.active_warnings : [];
  const warnByAgent = {};
  warnRows.forEach((r) => {
    const k = r.EmployeeUserID;
    if (!warnByAgent[k]) warnByAgent[k] = { _key: k, EmployeeName: r.EmployeeName || '', Total: 0 };
    warnByAgent[k][r.WarningNumber] = (warnByAgent[k][r.WarningNumber] || 0) + r.Qty;
    warnByAgent[k].Total += r.Qty;
  });
  const warnPivot = Object.values(warnByAgent).sort((a, b) => a.EmployeeName.localeCompare(b.EmployeeName));
  const warnTotals = {};
  let warnGrand = 0;
  warnRows.forEach((r) => {
    warnTotals[r.WarningNumber] = (warnTotals[r.WarningNumber] || 0) + r.Qty;
    warnGrand += r.Qty;
  });

  return (
    <>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card title="Incidents by month" size="small">
            {byMonth.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byMonth} margin={{ left: 0, right: 16, top: 8, bottom: 4 }}>
                  <XAxis dataKey="MonthKey" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <CartesianGrid strokeDasharray="3 3" />
                  <Tooltip />
                  <Bar dataKey="Total" fill="#d46b08" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty description="No incidents in this window" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          {/* Counts incidents, not incident-issue pairs, so these add to more
              than the monthly totals: one incident can raise several issues. */}
          <Card title="Incidents by issue" size="small">
            <Table
              rowKey="IssueName"
              dataSource={byIssue}
              size="small"
              pagination={false}
              sticky
              locale={{ emptyText: 'No incidents in this window' }}
              columns={[
                { title: 'Issue', dataIndex: 'IssueName', key: 'IssueName',
                  sorter: (a, b) => (a.IssueName ?? '').localeCompare(b.IssueName ?? '') },
                { title: 'Incidents', dataIndex: 'Incidents', key: 'Incidents', width: 105, align: 'right',
                  defaultSortOrder: 'descend',
                  sorter: (a, b) => a.Incidents - b.Incidents },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24}>
          <Card title="Active Warning (date range is not applied)" size="small">
            <Table
              rowKey="_key"
              dataSource={warnPivot}
              size="small"
              pagination={false}
              bordered
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No active warnings.' }}
              columns={[
                { title: 'Agent Name', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 240,
                  sorter: (a, b) => a.EmployeeName.localeCompare(b.EmployeeName),
                  // A link only for those who can open the agent summary.
                  render: (v, r) => canViewAgent
                    ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r._key)}`)}>{v}</Typography.Link>
                    : v },
                ...WARNINGS.map((w) => ({
                  title: w, key: w, width: 90, align: 'center',
                  render: (_, r) => r[w] ? r[w] : <Typography.Text type="secondary">—</Typography.Text>,
                })),
                { title: 'Total', dataIndex: 'Total', key: 'Total', width: 90, align: 'right',
                  sorter: (a, b) => a.Total - b.Total,
                  render: (v) => <Typography.Text strong>{v}</Typography.Text> },
              ]}
              summary={warnPivot.length > 0 ? () => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
                  {WARNINGS.map((w, i) => (
                    <Table.Summary.Cell key={w} index={1 + i} align="center">
                      <Typography.Text strong>{warnTotals[w] || 0}</Typography.Text>
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={1 + WARNINGS.length} align="right">
                    <Typography.Text strong>{warnGrand}</Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : undefined}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
